import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import {
  PaymentDerivedStatus,
  RecurringOccuranceType,
} from '@fitness-force/enum';
import { CurrencyformatPipe } from '@fitness-force/pipes/currency';
import { DepositTranslatePipe } from '@fitness-force/pipes/common-pipes';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import type { InvoiceOccurance } from '@fitness-force/models';
import { ToastMessageService, DepositTerminologyService } from '@fitness-force/api';
import { BadgeModule } from 'primeng/badge';
import { StyleClassModule } from 'primeng/styleclass';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { BehaviorSubject, Observable } from 'rxjs';

@Component({
  selector: 'invoice-payment-amount',
  templateUrl: './invoice-payment-amount.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ToastMessageService],
  standalone: true,
  imports: [
    StyleClassModule,
    TooltipModule,
    BadgeModule,
    TagModule,
    StyleClassModule,
    CurrencyformatPipe,
    AsyncPipe,
    TranslateModule,
    DepositTranslatePipe,
  ],
})
export class InvoicePaymentAmountComponent {
  translate = inject(TranslateService);
  private readonly depositTerminology = inject(DepositTerminologyService);

  columnData: Observable<any> = new BehaviorSubject(null);
  additionData: Observable<any> = new BehaviorSubject(null);

  public get paymentStatus() {
    return PaymentDerivedStatus;
  }

  public get recurringOccuranceType() {
    return RecurringOccuranceType;
  }

  getTooltipMessage(OcurenctType: InvoiceOccurance) {
    let message: string = '';
    const data =
      RecurringOccuranceType[
        OcurenctType.occurance_type.toString() as keyof typeof RecurringOccuranceType
      ];
    switch (data) {
      case RecurringOccuranceType.Deposit:
        message = this.depositTerminology.instant('DEPOSIT.ADDED_ON_OCCURRENCE');
        break;
      case RecurringOccuranceType.EndProrata:
        this.translate
          .get('SUBSCRIPTIONS.PAYMENT_TIMELINE.ERROR_MSG')
          .subscribe((translations: any) => {
            message = translations;
          });
        break;
      case RecurringOccuranceType.Freeze:
        this.translate
          .get('SUBSCRIPTIONS.PAYMENT_TIMELINE.FREEZE_CYCLE_MSG')
          .subscribe((translations: any) => {
            message = translations;
          });
        break;
      case RecurringOccuranceType.InitialProRata:
        this.translate
          .get('SUBSCRIPTIONS.PAYMENT_TIMELINE.ERROR_MSG')
          .subscribe((translations: any) => {
            message = translations;
          });

        break;
      case RecurringOccuranceType.MiddCycleAddon:
        this.translate
          .get('SUBSCRIPTIONS.PAYMENT_TIMELINE.MID_CYCLE_ADDON_MSG')
          .subscribe((translations: any) => {
            message = translations;
          });
        break;
      case RecurringOccuranceType.MiddCycleDiscount:
        this.translate
          .get('SUBSCRIPTIONS.PAYMENT_TIMELINE.MID_CYCLE_DIS_MSG')
          .subscribe((translations: any) => {
            message = translations;
          });
        break;
      case RecurringOccuranceType.Recurring:
        this.translate
          .get('SUBSCRIPTIONS.PAYMENT_TIMELINE.ERROR_MSG')
          .subscribe((translations: any) => {
            message = translations;
          });
        break;
      case RecurringOccuranceType.BillingStopped:
        this.translate
          .get('SUBSCRIPTIONS.PAYMENT_TIMELINE.BILLING_STOPPED_MSG')
          .subscribe((translations: string) => {
            message = translations;
          });
        break;
      case RecurringOccuranceType.Enhancement:
        this.translate
          .get('SUBSCRIPTIONS.PAYMENT_TIMELINE.ENHANCEMENT_MSG')
          .subscribe((translations: string) => {
            message = translations;
          });
        break;

    }
    return message;
  }

  isBillingStoppedOccurance(timeline: InvoiceOccurance): boolean {
    return (
      timeline.occurance_type === RecurringOccuranceType.BillingStopped ||
      timeline.occurance_type?.toString() ===
        RecurringOccuranceType[RecurringOccuranceType.BillingStopped]
    );
  }

  isSuccessfulPayment(timeline: InvoiceOccurance): boolean {
    return (
      timeline.payment_status === this.paymentStatus.Successfull ||
      timeline.payment_status?.toString() ===
        this.paymentStatus[this.paymentStatus.Successfull]
    );
  }

  isfailedPayment(timeline: InvoiceOccurance): boolean {
    return (
      timeline.payment_status === this.paymentStatus.Failed ||
      timeline.payment_status?.toString() ===
        this.paymentStatus[this.paymentStatus.Failed]
    );
  }

  isOccurenceDiscount(timeline: InvoiceOccurance): boolean {
    return (
      timeline.occurance_type ===
        this.recurringOccuranceType.MiddCycleDiscount ||
      timeline.occurance_type?.toString() ===
        this.recurringOccuranceType[
          this.recurringOccuranceType.MiddCycleDiscount
        ]
    );
  }

  isCreatedPayment(timeline: InvoiceOccurance): boolean {
    return (
      timeline.payment_status === this.paymentStatus.Created ||
      timeline.payment_status?.toString() ===
        this.paymentStatus[this.paymentStatus.Created] ||
      timeline.payment_status == PaymentDerivedStatus.Initiated ||
      timeline.payment_status?.toString() ==
        PaymentDerivedStatus[PaymentDerivedStatus.Initiated]
    );
  }

  isFreezeOccurance(timeline: InvoiceOccurance): boolean {
    return (
      timeline.occurance_type === RecurringOccuranceType.Freeze ||
      timeline.occurance_type?.toString() ===
        RecurringOccuranceType[RecurringOccuranceType.Freeze]
    );
  }

  isEnhancementOccurance(timeline: InvoiceOccurance): boolean {
    return (
      this.resolveOccurrenceType(timeline.occurance_type) ===
      RecurringOccuranceType.Enhancement
    );
  }

  readonly enhancementTagStyleClass =
    'text-xxs py-0 px-2 ml-2 inline-flex w-auto white-space-nowrap bg-indigo-50 text-indigo-700 border-1 border-indigo-200';

  private resolveOccurrenceType(
    occuranceType: RecurringOccuranceType | string | number
  ): RecurringOccuranceType | undefined {
    if (typeof occuranceType === 'number') {
      return occuranceType as RecurringOccuranceType;
    }

    return RecurringOccuranceType[
      occuranceType.toString() as keyof typeof RecurringOccuranceType
    ];
  }

  shouldShowIcon(timeline: any): boolean {
    let result: boolean = false;
    const data =
      RecurringOccuranceType[
        timeline.occurance_type.toString() as keyof typeof RecurringOccuranceType
      ];
    switch (data) {
      case RecurringOccuranceType.Deposit:
        result = true;
        break;
      case RecurringOccuranceType.EndProrata:
        result = false;

        break;
      case RecurringOccuranceType.Freeze:
        result = true;
        break;
      case RecurringOccuranceType.InitialProRata:
        result = false;
        break;
      case RecurringOccuranceType.MiddCycleAddon:
        result = true;
        break;
      case RecurringOccuranceType.MiddCycleDiscount:
        result = true;
        break;
      case RecurringOccuranceType.Recurring:
        result = false;
        break;
      case RecurringOccuranceType.BillingStopped:
        result = true;
        break;
      case RecurringOccuranceType.Enhancement:
        result = true;
        break;
    }
    return result;
  }

  showBadgeInStatusNoneCase(OcurenctType: RecurringOccuranceType | string | number) {
    let show: boolean = false;
    const data = this.resolveOccurrenceType(OcurenctType);
    switch (data) {
      case RecurringOccuranceType.Deposit:
        show = false;
        break;
      case RecurringOccuranceType.EndProrata:
        show = false;
        break;
      case RecurringOccuranceType.Freeze:
        show = true;
        break;
      case RecurringOccuranceType.InitialProRata:
        show = false;
        break;
      case RecurringOccuranceType.MiddCycleAddon:
        show = true;
        break;
      case RecurringOccuranceType.MiddCycleDiscount:
        show = true;
        break;
      case RecurringOccuranceType.Recurring:
        show = false;
        break;
      case RecurringOccuranceType.Enhancement:
        show = true;
        break;
      case RecurringOccuranceType.BillingStopped:
        show = true;
        break;
    }
    return show;
  }
}
